import { FC } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Box, FormControl, FormHelperText, FormLabel, Input, rem, Stack, Textarea } from '@bonusly/design-system';

import { User } from '../../../../../schemas/users';
import { CountHelpText } from '../../../../form/count-help-text';
import { FormErrorMessage } from '../../../../form/form-error-message';
import { UserUsernameSelector } from '../../../../form/search/user-username-selector';

export type InviteFormData = {
	subject: string;
	title: string;
	body: string;
	inviteList: User[];
};

type InviteFormProps = {
	form: ReturnType<typeof useForm<InviteFormData>>;
	onSubmit: (data: InviteFormData) => void;
	onSearchUsers: (query: string) => Promise<User[]>;
};

export const InviteForm: FC<InviteFormProps> = ({ form, onSubmit, onSearchUsers }) => {
	const { t } = useTranslation(['recognition']);

	const {
		formState: { errors },
		handleSubmit,
		register,
		watch,
		setValue,
		trigger,
	} = form;

	const [subject, title, body, inviteList] = watch(['subject', 'title', 'body', 'inviteList']);

	const handleInviteListBlur = async () => {
		// Trigger validation for the inviteList field to clear errors if it's now valid
		// Let's wait for the form to be updated with the new inviteList
		setTimeout(async () => {
			await trigger('inviteList');
		}, 100);
	};

	return (
		<FormProvider {...form}>
			<Stack as="form" onSubmit={handleSubmit(onSubmit)} spacing={4} pt={4}>
				<FormControl position="relative" isInvalid={!!errors.inviteList}>
					<FormLabel>{t('invite_others_modal.invite_list_label')}</FormLabel>
					<UserUsernameSelector
						value={inviteList || []}
						onChange={(users) => {
							setValue('inviteList', users);
							trigger('inviteList');
						}}
						onSearch={onSearchUsers}
						onBlur={handleInviteListBlur}
					/>
					<FormErrorMessage>{errors.inviteList?.message}</FormErrorMessage>
					<FormHelperText>{t('invite_others_modal.invite_list_helper')}</FormHelperText>
				</FormControl>

				<FormControl isInvalid={!!errors.subject}>
					<FormLabel>{t('invite_others_modal.subject_label')}</FormLabel>
					<Input type="text" {...register('subject')} py={rem(23)} px={rem(8)} borderRadius="md" borderColor="text.softest" />
					<FormErrorMessage>{errors.subject?.message}</FormErrorMessage>
					<CountHelpText count={subject.trim().length} max={70}>
						{t('invite_others_modal.subject_helper')}
					</CountHelpText>
				</FormControl>

				<FormControl isInvalid={!!errors.title}>
					<FormLabel>{t('invite_others_modal.title_label')}</FormLabel>
					<Input type="text" {...register('title')} py={rem(23)} px={rem(8)} borderRadius="md" borderColor="text.softest" />
					<FormErrorMessage>{errors.title?.message}</FormErrorMessage>
					<CountHelpText count={title.trim().length} max={40}>
						{t('invite_others_modal.title_helper')}
					</CountHelpText>
				</FormControl>

				<FormControl isInvalid={!!errors.body} pb={2}>
					<FormLabel>{t('invite_others_modal.body_label')}</FormLabel>
					<Textarea {...register('body')} px={rem(8)} borderRadius="md" borderColor="text.softest" rows={6} />
					<FormErrorMessage>{errors.body?.message}</FormErrorMessage>
					<CountHelpText count={body.trim().length} max={400}>
						<Box pr={10}>{t('invite_others_modal.body_helper')}</Box>
					</CountHelpText>
				</FormControl>
			</Stack>
		</FormProvider>
	);
};
